import React, { useEffect } from 'react';
import { Modal, Select, Input, Button, Alert, Form } from 'antd';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCancelRequest } from '../../hooks/useCancelRequest';
import { useHomeStrings } from '../../hooks/useHomeStrings';
import { telegram } from '../../../../utils/telegram';
import { CANCELLATION_REASONS } from '../../../../types/client';
import type { CancellationReason } from '../../../../types/client';

const cancelSchema = z.object({
  reason: z.enum(CANCELLATION_REASONS, {
    required_error: 'Sabab tanlang',
    invalid_type_error: 'Sabab tanlang',
  }),
  comment: z.string().max(500, "500 belgidan oshmasin").optional(),
});

type CancelFormValues = z.infer<typeof cancelSchema>;

interface Props {
  bookingUuid: string;
  open: boolean;
  onClose: () => void;
}

export const CancelRequestModal: React.FC<Props> = ({ bookingUuid, open, onClose }) => {
  const STRINGS = useHomeStrings();
  const REASON_LABELS: Record<CancellationReason, string> = {
    PLANS_CHANGED: STRINGS.REASON_PLANS_CHANGED,
    NEED_DIFFERENT_TIME: STRINGS.REASON_NEED_DIFFERENT_TIME,
    CANNOT_ATTEND: STRINGS.REASON_CANNOT_ATTEND,
    OTHER: STRINGS.REASON_OTHER,
  };
  const { submit, isSubmitting, error, isDone, reset } = useCancelRequest();

  const {
    control,
    handleSubmit,
    formState: { isValid, isDirty },
    reset: resetForm,
  } = useForm<CancelFormValues>({
    resolver: zodResolver(cancelSchema),
    mode: 'onChange',
  });

  // Modal yopilganda state va form reset
  useEffect(() => {
    if (!open) {
      resetForm();
      reset();
    }
  }, [open, resetForm, reset]);

  // Muvaffaqiyatdan keyin modal yopish + haptic
  useEffect(() => {
    if (isDone) {
      telegram.hapticFeedback.notification('success');
      onClose();
    }
  }, [isDone, onClose]);

  const onSubmit = (values: CancelFormValues) => {
    void submit(bookingUuid, values.reason);
  };

  return (
    <Modal
      title={STRINGS.CANCEL_MODAL_TITLE}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Form.Item label={STRINGS.CANCEL_REASON_LABEL} required>
          <Controller
            name="reason"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Select
                  {...field}
                  placeholder={STRINGS.CANCEL_REASON_PLACEHOLDER}
                  style={{ width: '100%' }}
                  status={fieldState.error ? 'error' : undefined}
                  options={CANCELLATION_REASONS.map((r) => ({
                    value: r,
                    label: REASON_LABELS[r],
                  }))}
                />
                {fieldState.error && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                    {fieldState.error.message}
                  </div>
                )}
              </>
            )}
          />
        </Form.Item>

        <Form.Item label={STRINGS.CANCEL_NOTE_LABEL}>
          <Controller
            name="comment"
            control={control}
            render={({ field, fieldState }) => (
              <>
                <Input.TextArea
                  {...field}
                  placeholder={STRINGS.CANCEL_NOTE_PLACEHOLDER}
                  rows={3}
                  maxLength={500}
                  showCount
                  status={fieldState.error ? 'error' : undefined}
                />
                {fieldState.error && (
                  <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
                    {fieldState.error.message}
                  </div>
                )}
              </>
            )}
          />
        </Form.Item>

        {error && (
          <Alert
            type="error"
            message={error}
            style={{ marginBottom: 12 }}
            showIcon
          />
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onClose} disabled={isSubmitting}>
            {STRINGS.CANCEL_CLOSE}
          </Button>
          <Button
            type="primary"
            danger
            htmlType="submit"
            loading={isSubmitting}
            disabled={!isDirty || !isValid || isSubmitting}
          >
            {STRINGS.CANCEL_SUBMIT}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
